import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faTrash, 
  faCalendarTimes,
  faMapMarkerAlt,
  faClock,
  faCalendarDay,
  faExclamationTriangle,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';

const BlockedDaysList = ({ blockedDays, onEdit, onDelete, formatDate }) => {
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  if (!blockedDays || blockedDays.length === 0) {
    return (
      <div className="data-table-container">
        <div className="empty-state">
          <FontAwesomeIcon icon={faCalendarDay} size="3x" style={{color: '#ccc', marginBottom: '1rem'}} />
          <h3>No Blocked Days</h3>
          <p>No calendar days are currently blocked. Click "Block Calendar Days" to add some.</p>
        </div>
      </div>
    );
  }

  const handleDeleteClick = (blockedDay) => {
    setDeleteConfirm(blockedDay.id);
  };

  const handleDeleteConfirm = (blockedDay) => {
    onDelete(blockedDay);
    setDeleteConfirm(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const isDatePast = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateString) < today;
  };

  const isDateToday = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const blockDate = new Date(dateString);
    blockDate.setHours(0, 0, 0, 0);
    return blockDate.getTime() === today.getTime();
  };

  const getDateStatus = (dateString) => {
    if (isDateToday(dateString)) {
      return { status: 'today', icon: faExclamationTriangle, color: 'orange' };
    } else if (isDatePast(dateString)) {
      return { status: 'past', icon: faCheckCircle, color: 'gray' };
    } else {
      return { status: 'future', icon: faCalendarTimes, color: 'red' };
    }
  };

  const getLocationDisplay = (location) => {
    switch (location) {
      case 'All':
        return 'All Locations';
      case 'Angola':
        return 'Angola Office';
      case 'Elkhart':
        return 'Elkhart Office';
      default:
        return location;
    }
  };

  const getTimeDisplay = (blockedDay) => {
    if (blockedDay.allDay || !blockedDay.startTime || !blockedDay.endTime) {
      return 'All Day';
    }
    return `${blockedDay.startTime} - ${blockedDay.endTime}`;
  };

  // Group blocked days by status
  const upcomingDays = blockedDays.filter(day => !isDatePast(day.date) && !isDateToday(day.date));
  const todayDays = blockedDays.filter(day => isDateToday(day.date));
  const pastDays = blockedDays.filter(day => isDatePast(day.date));

  const renderBlockedDayRow = (blockedDay) => {
    const dateStatus = getDateStatus(blockedDay.date);
    
    return (
      <tr key={blockedDay.id} className={dateStatus.status === 'past' ? 'past-blocked-day' : ''}>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon 
              icon={dateStatus.icon} 
              style={{ color: dateStatus.color, fontSize: '0.9rem' }} 
            />
            <div>
              <div style={{ fontWeight: 500 }}>{formatDate(blockedDay.date)}</div>
              <div style={{ fontSize: '0.8rem', color: '#666' }}>{blockedDay.date}</div>
            </div>
          </div>
        </td>
        
        <td>
          <strong>{blockedDay.reason}</strong>
        </td>
        
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faClock} style={{ color: '#666', fontSize: '0.8rem' }} />
            <span>{getTimeDisplay(blockedDay)}</span>
          </div>
        </td>
        
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#666', fontSize: '0.8rem' }} />
            <span>{getLocationDisplay(blockedDay.location)}</span>
          </div>
        </td>
        
        <td>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>
            {blockedDay.createdAt ? formatDate(blockedDay.createdAt) : 'Unknown'}
          </div>
        </td>
        
        <td>
          <div className="actions-cell">
            {deleteConfirm === blockedDay.id ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.8rem', marginRight: '0.5rem' }}>Delete?</span>
                <button
                  className="icon-button delete"
                  onClick={() => handleDeleteConfirm(blockedDay)}
                  title="Confirm delete"
                >
                  Yes
                </button>
                <button
                  className="icon-button cancel"
                  onClick={handleDeleteCancel}
                  title="Cancel delete"
                >
                  No
                </button>
              </div>
            ) : (
              <>
                <button
                  className="icon-button edit"
                  onClick={() => onEdit(blockedDay)}
                  title="Edit blocked day"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button
                  className="icon-button delete"
                  onClick={() => handleDeleteClick(blockedDay)}
                  title="Delete blocked day"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <style>
        {`
          .blocked-days-section {
            margin-bottom: 2rem;
          }
          .blocked-days-section h3 {
            margin-bottom: 1rem;
            color: #333;
            font-size: 1.1rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem 0;
            border-bottom: 1px solid #f0f0f0;
          }
          .blocked-days-section h3.today {
            color: #fb8c00;
          }
          .blocked-days-section h3.upcoming {
            color: #d32f2f;
          }
          .blocked-days-section h3.past {
            color: #43a047;
          }
          .past-blocked-day {
            opacity: 0.7;
          }
          .blocked-days-section details summary {
            cursor: pointer;
            font-weight: 500;
            padding: 0.75rem 0;
            border-bottom: 1px solid #f0f0f0;
            color: #43a047;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          .blocked-days-section details[open] summary {
            margin-bottom: 1rem;
          }
        `}
      </style>
      
      {/* Today's Blocked Days */}
      {todayDays.length > 0 && (
        <div className="blocked-days-section">
          <h3 className="today">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Today's Blocked Days ({todayDays.length})
          </h3>
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Reason</th>
                  <th>Time</th>
                  <th>Location</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {todayDays.map(renderBlockedDayRow)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upcoming Blocked Days */}
      {upcomingDays.length > 0 && (
        <div className="blocked-days-section">
          <h3 className="upcoming">
            <FontAwesomeIcon icon={faCalendarTimes} />
            Upcoming Blocked Days ({upcomingDays.length})
          </h3>
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Reason</th>
                  <th>Time</th>
                  <th>Location</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {upcomingDays.map(renderBlockedDayRow)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Past Blocked Days */}
      {pastDays.length > 0 && (
        <div className="blocked-days-section">
          <details>
            <summary>
              <FontAwesomeIcon icon={faCheckCircle} />
              Past Blocked Days ({pastDays.length}) - Click to expand
            </summary>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Reason</th>
                    <th>Time</th>
                    <th>Location</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pastDays.map(renderBlockedDayRow)}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}
    </>
  );
};

export default BlockedDaysList;